import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateUser } from "@/hooks/user";
import { RoleSelector } from "@/components/ui";
import { useTranslation } from 'react-i18next';
import React, { type FormEvent, useState } from "react";

interface UserAddModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const UserAddModal: React.FC<UserAddModalProps> = ({
  isOpen,
  closeModal,
}) => {
  const { t } = useTranslation();
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const createUserMutation = useCreateUser();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const createData = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        roles: selectedRoles
      };
      const data = await createUserMutation.mutateAsync(createData);
      if (data.success === true) {
        toast.success(t('settings.userCreatedSuccessfully'));
        closeModal();
        setUserData({
          firstName: '',
          lastName: '',
          email: '',
        });
        setSelectedRoles([]);
      }
    } catch (error) {
      toast.error(t('settings.creatingUserFailed'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:rounded">
        <DialogHeader>
          <DialogTitle>{t('settings.addUser')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.firstName')}
            </label>
            <input
              name="firstName"
              value={userData.firstName}
              onChange={handleInputChange}
              className="main-input"
              placeholder={t('settings.enterFirstName')}
              required
            />
          </div>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.lastName')}
            </label>
            <input
              name="lastName"
              value={userData.lastName}
              onChange={handleInputChange}
              className="main-input"
              placeholder={t('settings.enterLastName')}
              required
            />
          </div>
          <div className="mb-4">
            <label className="main-label">
              {t('settings.email')}
            </label>
            <input
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              type="email"
              className="main-input"
              placeholder={t('settings.enterYourEmail')}
              required
            />
          </div>
          <div className="mb-4">
            <RoleSelector
              selectedRoles={selectedRoles}
              onRolesChange={setSelectedRoles}
              label={t('settings.roles')}
              placeholder={t('settings.selectRoles')}
            />
          </div>
          <button
            type="submit"
            className="main-dark-button float-right"
          >
            {
              createUserMutation.isPending ? (
                <p>{t('common.creating')}</p>
              ) : (
                <p>{t('common.create')}</p>
              )
            }
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserAddModal;
